import { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { Barcode, MagnifyingGlass, Package, X, Camera } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useT } from '@/i18n/LanguageContext';
import { mfgApi, TYPE_BADGE } from './mfgApi';

const REGION = 'mfg-scan-region';

export default function ScanDialog({ open, onClose }) {
  const { t } = useT();
  const [code, setCode] = useState('');
  const [result, setResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);

  const doLookup = async (c) => {
    const v = (c ?? code).trim();
    if (!v) return;
    try {
      const r = await mfgApi.lookup(v);
      setResult(r);
      toast.success(r.type === 'item' ? t('mfg.scan.result.item') : t('mfg.scan.result.lot'));
    } catch (e) {
      setResult(null);
      toast.error(e?.response?.data?.detail || t('mfg.scan.notFound'));
    }
  };

  const stopScan = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); await scannerRef.current.clear(); } catch { /* noop */ }
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const startScan = async () => {
    setScanning(true);
    try {
      const mod = await import('html5-qrcode');
      const inst = new mod.Html5Qrcode(REGION);
      scannerRef.current = inst;
      await inst.start({ facingMode: 'environment' }, { fps: 10, qrbox: 220 },
        async (decoded) => { await stopScan(); setCode(decoded); doLookup(decoded); },
        () => { /* per-frame errors ignored */ });
    } catch {
      setScanning(false);
      toast.error(t('mfg.error'));
    }
  };

  useEffect(() => { if (!open) { stopScan(); setResult(null); setCode(''); } }, [open]); // eslint-disable-line

  const close = async () => { await stopScan(); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="scan-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Barcode size={20} className="text-orange-500" /> {t('mfg.scan.title')}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-zinc-500">{t('mfg.scan.hint')}</p>
          <div id={REGION} className={`${scanning ? 'block' : 'hidden'} rounded-lg overflow-hidden bg-black`} />
          {!scanning
            ? <Button variant="outline" onClick={startScan} data-testid="scan-start-btn" className="w-full h-10 border-orange-200 text-orange-600 hover:bg-orange-50"><Camera size={16} className="mr-1.5" /> {t('mfg.scan.start')}</Button>
            : <Button variant="outline" onClick={stopScan} data-testid="scan-stop-btn" className="w-full h-10"><X size={16} className="mr-1.5" /> {t('mfg.scan.stop')}</Button>}
          <div className="flex gap-2">
            <Input value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && doLookup()} placeholder={t('mfg.scan.manual')} className="h-10" data-testid="scan-manual-input" />
            <Button onClick={() => doLookup()} data-testid="scan-lookup-btn" className="h-10 bg-teal-700 hover:bg-teal-800"><MagnifyingGlass size={16} /></Button>
          </div>
          {result?.item && (
            <div className="rounded-lg border border-zinc-200 p-4" data-testid="scan-result">
              <div className="flex items-start gap-3">
                <Package size={22} className="text-orange-500 mt-0.5" weight="duotone" />
                <div className="min-w-0">
                  <p className="font-semibold text-zinc-800">{result.item.name}</p>
                  <p className="text-xs text-zinc-400 font-mono">{result.item.sku}</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <Badge variant="outline" className={TYPE_BADGE[result.item.type]}>{t(`mfg.type.${result.item.type}`)}</Badge>
                    <span className="text-sm text-zinc-700">{t('mfg.item.onHand')}: <b>{result.item.on_hand} {result.item.uom}</b></span>
                  </div>
                  {result.type === 'lot' && result.lot && (
                    <p className="text-xs text-zinc-500 mt-1">{t('mfg.lot.no')}: <span className="font-mono">{result.lot.lot_no}</span> · {result.lot.quantity} {result.item.uom}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
